/**
 * Helpers and constants for styling cells in Excel files exported from AgGrid.
 * We'd like to have a consistent style throughout the Budget module at least, so we
 * regroup them here.
 */
import {
    CellClassFunc,
    ExcelExportParams,
    ExcelStyle,
    IRowNode,
    ValueFormatterParams,
} from 'ag-grid-community';

const STRING_EXCEL_CLASSNAME = 'stringFormat';
const BOLD_STRING_EXCEL_CLASSNAME = 'boldStringFormat';

/** Unfortunately, cellClass is shared for CSS classes and Excel formatting. It seems
 * that passing a cellClass will override some classes injected by AgGrid related to colDef
 * params like "type". In this case, we lose the "ag-right-aligned-cell" passed by "type: numericColumn"
 * which breaks cell alignment in the UI, for projections grid. A better fix would be to stop adding
 * display: flex to cells of ProjectionGrid (and ExpenseIncome)
 */
const BOLD_PERCENTAGE_EXCEL_CLASSNAMES = [
    'boldPercentageFormat',
    'ag-right-aligned-cell',
];
const BOLD_CURRENCY_EXCEL_CLASSNAMES = [
    'boldCurrencyFormat',
    'ag-right-aligned-cell',
];
export const PERCENTAGE_EXCEL_CLASSNAMES = [
    'percentageFormat',
    'ag-right-aligned-cell',
];
export const CURRENCY_EXCEL_CLASSNAMES = [
    'currencyFormat',
    'ag-right-aligned-cell',
];

/**
 * Check if the node is part of a group row, meaning that it represents
 * an aggregation (e.g. a total or sub-total row) instead of a leaf node
 */
const isGroupRow = (node: IRowNode | null) => !node?.leafGroup;

export const getBudgetExcelCurrencyCellClass = ({
    node,
}: ValueFormatterParams): string[] => {
    return isGroupRow(node)
        ? BOLD_CURRENCY_EXCEL_CLASSNAMES
        : CURRENCY_EXCEL_CLASSNAMES;
};

export const getBudgetExcelPercentageCellClass = ({
    node,
}: ValueFormatterParams): string[] => {
    return isGroupRow(node)
        ? BOLD_PERCENTAGE_EXCEL_CLASSNAMES
        : PERCENTAGE_EXCEL_CLASSNAMES;
};

export const getBudgetExcelStringCellClass: CellClassFunc = ({ node }) => {
    return isGroupRow(node)
        ? BOLD_STRING_EXCEL_CLASSNAME
        : STRING_EXCEL_CLASSNAME;
};

const BOLD_LIGHT_GRAY: Partial<ExcelStyle> = {
    font: {
        bold: true,
    },
    interior: {
        pattern: 'Gray0625',
    },
};

export const BUDGET_EXCEL_STYLES: ExcelStyle[] = [
    {
        id: CURRENCY_EXCEL_CLASSNAMES[0],
        numberFormat: { format: '#,##0.00' },
    },
    {
        id: BOLD_CURRENCY_EXCEL_CLASSNAMES[0],
        numberFormat: { format: '#,##0.00' },
        ...BOLD_LIGHT_GRAY,
    },
    {
        id: PERCENTAGE_EXCEL_CLASSNAMES[0],
        numberFormat: { format: '#,##0.00%' },
    },
    {
        id: BOLD_PERCENTAGE_EXCEL_CLASSNAMES[0],
        numberFormat: { format: '#,##0.00%' },
        ...BOLD_LIGHT_GRAY,
    },
    {
        id: BOLD_STRING_EXCEL_CLASSNAME,
        dataType: 'String',
        ...BOLD_LIGHT_GRAY,
    },
    {
        id: STRING_EXCEL_CLASSNAME,
        dataType: 'String',
    },
    {
        id: 'header',
        font: {
            bold: true,
        },
    },
];

export const BUDGET_EXCEL_EXPORT_PARAMS: ExcelExportParams = {
    freezeRows: 'headers',
};
